import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {ICreateExpenseData, ICustomer, IExpense, IVendor} from '../../../@types';
import BackArrow from '../../../assets/icons/backArrow';
import { manageExpense, validateExpenseBasicInfo,  validateExpenseItemDetails,  validateExpenseTotalPayment } from '../../../modules/expense/expense';
import { LineItemTable, TableColumn } from '../../common/LineItemTable';
import {
  EXPENSE_CATEGORY,
  EXPENSE_PAYMENT_CURRENCY_LIST,
  PAYMENT_PROVIDER_LIST,
  TABLE_HEAD,
} from '../../../constants';
import { clearFieldError } from '../../../utils/handleErrors';
import { useCustomerSearch, useVendorSearch } from '../../../hooks';
import useLineItems from "../../../modules/lineItem";
import StepForm from '../../core/MultiStepForm/StepForm';
import { getBudgetHistory } from '../../../backend-services';
import ExpenseDetail from './AddExpenseForm/ExpenseDetail';
import ExpenseDetailTwo from './AddExpenseForm/ExpenseDetailTwo';
import ExpenseDetailThree from './AddExpenseForm/ExpenseDetailThree';
import PlusBoxIcon from '../../../assets/icons/PlusBoxIcon';
import { ImportantCard } from '../../common/TooltipCards/ImportantCard';

const dropDownCurrency = EXPENSE_PAYMENT_CURRENCY_LIST;

const dropDownCategory = EXPENSE_CATEGORY;

const dropDownPaymentMethod = PAYMENT_PROVIDER_LIST;

const columns: TableColumn[] = TABLE_HEAD;

type Props = {
  expense: ICreateExpenseData;
  expenses?: IExpense[];
};

const ExpenseForm: React.FC<Props> = ({ expense }) => {
  const navigate = useNavigate();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [total, setTotal] = React.useState<number>(0);
  const [customer, setCustomer] = React.useState<ICustomer | undefined>();
  const [vendorName, setVendorName] = React.useState<IVendor | undefined>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isBillable, setIsBillable] = React.useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState<boolean>(false);
  const [, setMessage] = React.useState<string>('');
  const [expenseId, setExpenseId] = React.useState<string>('');
  const [updatedExpenseId, setUpdatedExpenseId] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [showRateInput, setShowRateInput] = useState(false);
  const [currencyRate, setCurrencyRate] = useState('');
  const [selectedCurrencyLabel, setSelectedCurrencyLabel] = React.useState('');
  
  const [formData, setFormData] = React.useState<ICreateExpenseData>({
    customerId: undefined,
    category: '',
    currency: '',
    date: new Date().toISOString().substring(0, 10),
    description: '',
    lineItems: [],
    amount: 0,
    additionalInformation: "",
    notes:"",
    isBillable: false,
isExpense:true,
  });
  const [validationResults, setValidationResults] = React.useState<ICreateExpenseData>({
    customerId: '',
    date: '',
    lineItems: [],
    category: '',
    currency: '',
    description: '',
    vendorName: '',
    amount: 0,
    paymentMethod: '',
    isBillable: false,
  
  });
  const { items, addItem, setItems, removeItem, updateCell, updateRow } = useLineItems(
    expense?.lineItems.length ? expense.lineItems : [{}] 
  );
  
 

  const handleNextTab = () => {
    let isValid = true;
  
    // Validate based on the current tab
    if (currentTab === 0) {
      isValid = validateExpenseBasicInfo(formData, setValidationResults);
    } else if (currentTab === 1) {
      isValid = validateExpenseItemDetails(formData, setValidationResults);
    } else if (currentTab === 2) {
      isValid = validateExpenseTotalPayment(formData, setValidationResults);
    }
  
    if (isValid) {
      setCurrentTab(currentTab + 1);
    }
  };


  const { data: vendors, isSearching: isVendorSearching, handleSearch: handleVendorSearch } = useVendorSearch();
  
  const handleSelectedVendor = (selectedVendor: IVendor | null) => {
    if (selectedVendor) {
      setVendorName(selectedVendor);
      if (selectedVendor.name) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          vendorId: selectedVendor.id,
          vendorName: selectedVendor.name,
        }));
      }
      setValidationResults((prevValidationResults) => ({
        ...prevValidationResults,
        vendorName: '',
      }));
    } else {
      setVendorName(undefined);
      setFormData((prevFormData) => ({
        ...prevFormData,
        vendorName: '',
      }));
      setValidationResults((prevValidationResults) => ({
        ...prevValidationResults,
        vendorName: '',
      }));
      clearFieldError('selectedVendor', validationResults, setValidationResults);
    }
  };

  const handleDropDownChange = (name: string, value: string) => {
    clearFieldError(name, validationResults, setValidationResults);
    const selectedLabel = EXPENSE_PAYMENT_CURRENCY_LIST.find((currency) => currency.value === value)?.label || '';
  
    setFormData({ ...formData, [name]: value });
    setSelectedCurrencyLabel(selectedLabel); 
    setShowRateInput(value !== 'NGN');
  };
  

  const handleInputChange = (event: { target: { name: any; value: any } }): void => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));


    const updatedItems = items.map((item) => {
      if (item.id === name) {
        return { ...item, quantity: value };
      }
      return item;
    });
    // @ts-ignore
    setItems(updatedItems);
    setFormData((prevState: ICreateExpenseData) => ({
      ...prevState,
      lineItems: updatedItems,
    }));
    const _total = updatedItems.reduce((total, exp) => total + exp.rate || 0 || 0, 0);
    setTotal(_total);
    clearFieldError(name, validationResults, setValidationResults);
  };
  const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    clearFieldError(name, validationResults, setValidationResults);
  };


  React.useEffect(() => {
    setFormData((prevState: ICreateExpenseData) => ({
      ...prevState,
      lineItems: items.map(item => {
        return {
          ...item,
          amount: item.rate * item.quantity 
        }
      }),
    }));

    const total = items.reduce((total, exp) => total + exp.rate * exp.quantity || 0 || 0, 0);
    setTotal(total);
 
  }, [items]);

  const { data: customers, isSearching: isCustomerSearching, handleSearch: handleCustomerSearch } = useCustomerSearch();

  const handleSelectCustomer = (selectedCustomer: ICustomer | null) => {
    if (selectedCustomer) {
      setCustomer(selectedCustomer);
      if (selectedCustomer.id) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          customerId: selectedCustomer.id,
        }));
      }
      setValidationResults((prevValidationResults) => ({
        ...prevValidationResults,
        customerId: '',
      }));
    } else {
      setCustomer(undefined);
      setFormData((prevFormData) => ({
        ...prevFormData,
        customerId: '',
      }));
      setValidationResults((prevValidationResults) => ({
        ...prevValidationResults,
        customerId: '',
      }));
    }
  };

  

  const handleIsBillableChange = () => {
    setIsBillable(!isBillable); 
    setFormData((prevFormData) => ({
      ...prevFormData,
      isBillable: !isBillable,
    }));
  };
  const handleConfirmCreateExpense = async (event: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }
 
    let isValidated = true;

 
    isValidated = validateExpenseBasicInfo(formData, setValidationResults) && isValidated;
  
  
    isValidated = validateExpenseItemDetails(formData, setValidationResults) && isValidated;
 
     isValidated = validateExpenseTotalPayment(formData, setValidationResults) && isValidated;
  
    if (!isValidated) {
      if (formData.isBillable === true && !formData.customerId) {
        setValidationResults((prevValidationResults) => ({
          ...prevValidationResults,
          customerId: 'Customer selection is required for billable expenses',
        }));
      }
      if (!formData.vendorName) {
        setValidationResults((prevValidationResults) => ({
          ...prevValidationResults,
          vendorName: 'Vendor selection is required ',
        }));
      }
        formRef.current?.scrollIntoView({ behavior: 'smooth' });
        return;
    }
    
    try {
      setIsLoading(true);
      const updatedId = await manageExpense(
        {  expenseId: expense?.id,
          customer, 
          formData, 
          isBillable,
        });
      if (typeof updatedId === 'string') {
        setUpdatedExpenseId(updatedId);
      }
      
      setMessage('Expense update successful!');
      setShowSuccessModal(true);
      setIsLoading(false);
      navigate(`/expenses/notification/${updatedId}`);
    } catch (error: any) {
      console.error("Error:", error);
      if (error.response && (error.response.status === 400 || error.response.status === 500)) {
        navigate('/error');
      }
    } finally {
      setIsLoading(false);
    }

  }

  const handleCancel = () => {
    navigate('/expenses');
  };
  
  useEffect(() => {
    if (expense) {
      console.log('Fetched expense:', expense);
      setFormData((prevState) => ({
        ...prevState,
        notes: expense.notes,
        customerId: expense.customerId,
        customerName: expense.customerName,
        vendorName: expense.vendorName,
        amount: expense.amount,
        description: expense.description,
        paymentMethod: expense.paymentMethod,
        lineItems: expense.lineItems,
        currency: expense.currency,
        category: expense.category,
        isBillable:expense.isBillable
      }));
  
      const newItems = expense.lineItems.map((lineItem: any) => ({
        id: lineItem.id,
        name: lineItem.name,
        quantity: lineItem.quantity,
        rate: parseFloat(lineItem.rate)
      }));
      
      setItems(newItems);
  
      handleSelectedVendor({
        name: expense.vendorName as string,
        id: expense.vendorId as string
      });
    }
  }, [expense]);
  


  const handlePreviousTab = () => {
    if (currentTab > 0) {
      setCurrentTab(currentTab - 1);
    }
  };
 
  const stepInformation = [
    {
      title: "To add a new  vendor, Click ",
      subTitle: "New Vendor",
      icon: <PlusBoxIcon />,
      spanText1: " ",
      spanText2: "If it’s an existing customer,",
      spanText3: 'Enter the first three letters',
      spanText4: 'of the vendor’s name',
      spanText5: 'to select the customer',
    },
    {
      title: "To add a multiple items, Click",
      subTitle: "Add New Line",
      icon: <PlusBoxIcon />,
      spanText1: " ",
      spanText2: "",
      spanText3: '',
      spanText4: '',
      spanText5: '',
    },
    {
     
      spanText2: "If the expense is billable expense,Click on the toggle button and fill in the customer name. If not, Click on",
      spanText3: 'Save button ',
      spanText4: 'to create an expense.',
   
    },
  ];
  
  return (
    <>
      <div>
        <div className="flex items-center gap-[8.5px] ml-[-2em]">
          <a href="/expenses">
            <BackArrow />
          </a>
          <h1 className="font-bold">Add New Expense</h1>
        </div>
      </div>
      <div className=" hidden lg:block md:block flex justify-start  4xl:px-[9em]  3xl:px-[7em] 2xl:px-[6em] xl:px-[5em] biglg:px-[5em] lg:px-[4em] biglg:mt-[40px] xl:mt-[40px] lg:mt-[40px]  md:mt-[40px] ">
        <ImportantCard
  title={stepInformation[currentTab]?.title ?? ""}
  subTitle={stepInformation[currentTab]?.subTitle ?? ""}
  icon={stepInformation[currentTab]?.icon ? <PlusBoxIcon /> : undefined}
  spanText1={stepInformation[currentTab]?.spanText1 ?? ""}
  spanText2={stepInformation[currentTab]?.spanText2 ?? ""}
  spanText3={stepInformation[currentTab]?.spanText3 ?? ""}
  spanText4={stepInformation[currentTab]?.spanText4 ?? ""}
  spanText5={stepInformation[currentTab]?.spanText5 ?? ""}
/>

      </div>
      <StepForm
        stepTitles={["Basic Info", "Item Details", "Total Payment"]}
        nextButtonHandler={handleNextTab}
        backButtonHandler={handlePreviousTab}
        cancelButtonHandler={() => console.log("Cancel")}
        commonButtonText="Save"
        handleConfirmCreateExpense={handleConfirmCreateExpense}
        handleCancel={handleCancel}
        formData={formData}
        //@ts-ignore
        validationResults={validationResults}
         //@ts-ignore
        setValidationResults={setValidationResults}
        isLoading={isLoading}
      >
        <ExpenseDetail
         expense={expense}
          handleInputChange={handleInputChange}
          validationResults={validationResults}
          formData={formData}
          setFormData={setFormData}
          handleDropDownChange={handleDropDownChange}
          handleSelectedVendor ={handleSelectedVendor }
          validateCurrentStep={() => validateExpenseBasicInfo(formData, setValidationResults)}
          showRateInput={showRateInput}
          vendorName={vendorName}
          selectedCurrencyLabel={selectedCurrencyLabel} 
        />
  
        <ExpenseDetailTwo 
        formData={formData} 
        expense={expense} 
        setFormData={setFormData}  
         validateCurrentStep={() => validateExpenseItemDetails(formData, setValidationResults)}
         setValidationResults={setValidationResults}
         validationResults={validationResults}
         />
  
        <ExpenseDetailThree
          validationResults={validationResults}
          handleTextAreaChange={handleTextAreaChange}
          formData={formData}
          total={total}
          expense={expense}
          handleSelectCustomer={handleSelectCustomer}
          validateCurrentStep={() => validateExpenseTotalPayment(formData, setValidationResults)}
          customer={customer}
          isBillable={isBillable}
          handleIsBillableChange={handleIsBillableChange}
        />
      </StepForm>
    </>
  );
};
export { ExpenseForm };

  

